import { ChartOverviewObject } from 'src/interfaces/infoPages'

const renderLink = ({
  link,
  isEmail = false
}: {
  link: string
  isEmail: boolean
}) => (
  <a
    href={isEmail ? `mailto:${link}` : `${link}`}
    target='_blank'
    rel='noreferrer'
    style={{ color: ' #1D4289' }}
  >
    {link}
  </a>
)

const chartOverviewObjects: ChartOverviewObject[] = [
  {
    pageIntro: '',
    chartObjects: [
      {
        id: '21',
        chartDescriptionArray: [
          {
            text: ` Please check back periodically for
            updates and changes.`
          },
          {
            text: `Please read these Terms and Conditions carefully before using Our
            Platform. These Terms and Conditions constitute a contract between
            You and Company.`
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Interpretation and Definitions',
    chartObjects: [
      {
        chartName: 'Interpretation',
        id: '1.1',
        chartDescriptionArray: [
          {
            text: `The words of which the initial letter is capitalized have meanings defined hereunder.
            `
          },
          {
            text: `The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
            `
          }
        ]
      },
      {
        chartName: 'Definitions',
        id: '1.2',
        chartDescriptionArray: [
          {
            text: `For the purposes of these Terms and Conditions:.`,
            bullets: [
              {
                title: 'Affiliate',
                content:
                  'means an entity that controls, is controlled by or is under common control with a party, where “control” means ownership of 50% or more of the shares, equity interest or other securities entitled to vote for election of directors or other managing authority.'
              },
              {
                title: 'Company',
                content:
                  '(referred to as either “the Company”, “We”, “Us” or “Our”) means Opportunity at Work, Inc. and all its respective members, Affiliates, subsidiaries, directors, officers and employees.'
              },
              {
                title: 'Content',
                content:
                  'means all information, text, data, computer code, music, artwork, databases, graphics, images, sound recordings, audio and visual clips, logos, software, and other materials contained therein, and the compilation, collection, design, selection, and arrangement thereof on the Platform and Services. Content does not include any Third Party Materials or any features, opportunities, or services made available through third party websites.'
              },
              {
                title: 'Country',
                content: 'means the United States.'
              },
              {
                title: 'Feedback',
                content:
                  'means feedback, innovations or suggestions sent by You regarding the attributes, performance, features or other properties of or interactions and transactions or other Services enabled by Our Platform.'
              },
              {
                title: 'Individual Account',
                content:
                  'means an account created for each unique individual who may or may not be associated with an Organization Account.'
              },
              {
                title: 'Interaction',
                content:
                  'means an action or communication between two or more Users on or enabled by the Platform.'
              },
              {
                title: 'Organizational Account',
                content:
                  'means an account created for or on behalf of a business entity, such as a hiring employer or career coaches. An Organizational Account may cover multiple Users.'
              },
              {
                title: 'Linked Websites',
                content:
                  'means links to third-party websites or services that are not owned or controlled by the Company.'
              },
              {
                title: 'Platform',
                content:
                  'means the Website, any other Company online or offline offerings that link to these Terms and Conditions, and any associated Content.'
              },
              {
                title: 'Profiles',
                content:
                  'means self-identified information about the entity creating and using the Organization Account and/or the individual creating and using the Individual Account. Profiles may be visible to other Users on the Platform.'
              },
              {
                title: 'Services',
                content:
                  'means the services, features, functionality, and offers made available through the Platform.'
              },
              {
                title: 'Submission',
                content:
                  'means any information, communications, job listing, or material of any type or nature that You submit to or post on the Platform (or to any of Our pages on a social media platform or other website) by e-mail, posting, messaging, uploading, downloading, or otherwise.'
              },
              {
                title: 'Terms and Conditions',
                content:
                  "or 'Terms' means these Terms and Conditions that govern Your access to and use of the Platform."
              },
              {
                title: 'Third Party Materials',
                content:
                  'means any information, material, or functionality on the Platform and Services that is provided by third party content providers.'
              },
              {
                title: 'User',
                content:
                  '(referred to as either “the User”, “You”, or “Your”) means: a) the individual accessing the Platform and/or using the Services, b) any electronic agent accessing the Platform or using the Services on behalf of an individual or business entity, and c) the business entity on whose behalf an individual or electronic agent is accessing the Platform and/or using the Services.'
              },
              {
                title: 'Website',
                content: (
                  <>
                    refers to StarSight, located at{' '}
                    {renderLink({
                      link: 'https://www.stellarsight.org',
                      isEmail: false
                    })}
                  </>
                )
              }
            ]
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Acknowledgement',
    chartObjects: [
      {
        id: '2.1',
        chartDescriptionArray: [
          {
            text: `These are the Terms and Conditions governing Your access to and use of the Platform and the agreement that operates between You and the Company. These Terms and Conditions set out the rights and obligations of all Users regarding Your access to and use of the Platform, and Your access to and use of the Platform is conditioned on Your acceptance of and compliance with these Terms and Conditions. 
            By accessing, browsing, downloading, registering or using the Platform or the Services, You acknowledge that You accept and You agree to be bound by these Terms and Conditions in full. If You disagree with any part of these Terms and Conditions then You may not access the Platform or the Services.
            .`
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Ownership of the Platform and Access License',
    chartObjects: [
      {
        id: '2.3',
        chartDescriptionArray: [
          {
            text: `You understand and agree that Company owns, or (where required, appropriate, or applicable) has been licensed by third parties to use, all right, title, and interest in and to the Platform and the Services, and all Content, as well as the design, structure, selection, coordination, expression, “look and feel,” and arrangement of such Content. The Platform and Services will remain the exclusive property of the Company. 
            `
          },
          {
            text: `You acknowledge that the Platform and the Services are proprietary to Company and are protected by applicable intellectual property laws and other proprietary rights, laws, and treaties of both the Country and other countries, and that You acquire no ownership interest in the Platform or the Services by accessing and using either.
            `
          },
          {
            text: `You agree that Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company. 
            `
          },
          {
            text: `Company grants You a personal, non-exclusive, non-transferable, revocable, limited license to use the Platform and the Services solely for Your personal or internal use and subject to the condition that You do not (and do not allow any third party to) copy, modify, create a derivative work from, reverse engineer, reverse assemble, or otherwise attempt to discover any source code, sell, assign, sublicense, grant a security interest in, or otherwise transfer any right in or access to the Platform, the Services, or any other Content available via the Platform or the Services. All rights not expressly granted to You in these Terms and Conditions are reserved and retained by Company.  
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Use of the Platform and Services',
    chartObjects: [
      {
        id: '2.4',
        chartDescriptionArray: [
          {
            text: `Company reserves the right to do any of the following at any time without notice:  (i) modify, suspend, or terminate operation of or access to the Platform, the Services, or any portion thereof, for any reason; (ii) modify or change the Platform, the Services, or any portion thereof, and any applicable policies or terms; and (iii) interrupt the operation of the Platform, the Services, or any portion thereof, as necessary to perform routine or non-routine maintenance, perform error correction, or make other changes.
            You represent and agree that all information that You provide to Company in connection with Your access to and use of the Platform and the Services is and shall be true, accurate, and complete, to the best of Your knowledge, ability, and belief. 
            `
          },
          {
            text: `Any use or attempted use of the Platform and/or the Services (i) for any unlawful, unauthorized, fraudulent or malicious purpose; (ii) that could damage, disable, overburden, or impair any server, or the network(s) connected to any server; (iii) that could interfere with any other party's use and enjoyment of the Platform or the Services; (iv) to gain unauthorized access to any other accounts, computer systems or networks connected to any server or systems through hacking, password mining or any other means; (v) to access systems, data or information not intended by Company to be made accessible to a User; (vi) to attempt to obtain any materials or information through any means not intentionally made available by Us; or (vii) for any use other than the purpose for which it was intended, is prohibited.
            `
          },
          {
            text: 'In addition, in connection with Your use of the Platform and the Services, You agree You will not: ',
            bullets: [
              {
                content:
                  "Upload or transmit any message, information, data, text, software or images, or other content that is unlawful, immoral, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, or otherwise objectionable, or that may invade another's right of privacy or publicity."
              },
              {
                content:
                  'Create a false identity for the purpose of misleading others or impersonate any person or entity, including, without limitation, any Company representative, or falsely state or otherwise misrepresent your affiliation with a person or entity.'
              },
              {
                content:
                  'Upload or transmit any material that you do not have a right to reproduce, display or transmit under any law or under contractual or fiduciary relationships (such as nondisclosure agreements).'
              },
              {
                content:
                  "Upload files that contain viruses, Trojan horses, worms, time bombs, cancel-bots, corrupted files, or any other similar software or programs that may damage the operation of another's computer or property of another."
              },
              {
                content:
                  'Delete any author attributions, legal notices or proprietary designations or labels that you upload to any communication feature.'
              },
              {
                content:
                  'Use any of the Platform’s or Services’ communication features in a manner that adversely affects the availability of its resources to other Users (e.g., excessive shouting, use of all caps, or flooding continuous posting of repetitive text).'
              },
              {
                content:
                  "Upload or transmit any unsolicited advertising, promotional materials, 'junk mail', 'spam', 'chain letters', 'pyramid schemes' or any other form of solicitation, commercial or otherwise."
              },
              {
                content:
                  'Violate any applicable local, state, national or international law.'
              },
              {
                content:
                  'Upload or transmit any material that infringes any patent, trademark, service mark, trade secret, copyright or other proprietary rights of any party.'
              },
              {
                content:
                  'Delete or revise any material posted by any other person or entity.'
              },
              {
                content:
                  'Probe, scan, test the vulnerability of or breach the authentication measures of, the Platform or any related networks or systems.'
              },
              {
                content:
                  'Register, subscribe, attempt to register, attempt to subscribe, unsubscribe, or attempt to unsubscribe, any party for any products or services if you are not expressly authorized by such party to do so.'
              },
              {
                content:
                  'Harvest or otherwise collect information about others, including e-mail addresses.'
              },
              {
                content:
                  'Use any modified versions of the Platform or the Services, for any reason whatsoever, without the express written consent of the Company.'
              },
              {
                content:
                  'Attempt to gain any unauthorized access to the Platform, the Services or any of its associated Content, including computer systems, software, or networks.'
              },
              {
                content:
                  'Use any robot, spider, scraper, or other automated or manual means to access the Platform or the Services, or copy any Content or information thereon.'
              }
            ]
          },
          {
            text: 'Company reserves the right to take whatever lawful actions it may deem appropriate in response to actual or suspected violations of these Terms and Conditions, including, without limitation, the suspension or termination of Your access and/or Individual Account and/or Organizational Account. Company may cooperate with legal authorities and/or third parties in the investigation of any suspected or alleged crime or civil wrong. Company reserves the right at all times to disclose any information as Company deems necessary to satisfy any applicable law, regulation, legal process or governmental request, or to edit, refuse to post or to remove any information or materials, in whole or in part, in Company’s sole discretion. You also agree to reimburse Company for any damage, loss, cost or expense Company incurs (including fees or costs of attorneys, accountants, professional advisors, and other experts incurred in connection with the defense or settlement of the foregoing) because of Your use of the Platform or the Services for any unlawful or prohibited purpose.'
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Accounts, Profiles Interactions, Security',
    chartObjects: [
      {
        id: '2.5',
        chartDescriptionArray: [
          {
            text: `You understand and agree that Company owns, or (where required, appropriate, or applicable) has been licensed by third parties to use, all right, title, and interest in and to the Platform and the Services, and all Content, as well as the design, structure, selection, coordination, expression, “look and feel,” and arrangement of such Content. The Platform and Services will remain the exclusive property of the Company. 
            `
          },
          {
            text: `You acknowledge that the Platform and the Services are proprietary to Company and are protected by applicable intellectual property laws and other proprietary rights, laws, and treaties of both the Country and other countries, and that You acquire no ownership interest in the Platform or the Services by accessing and using either.
            `
          },
          {
            text: `You agree that Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company. 
            `
          },
          {
            text: `Company grants You a personal, non-exclusive, non-transferable, revocable, limited license to use the Platform and the Services solely for Your personal or internal use and subject to the condition that You do not (and do not allow any third party to) copy, modify, create a derivative work from, reverse engineer, reverse assemble, or otherwise attempt to discover any source code, sell, assign, sublicense, grant a security interest in, or otherwise transfer any right in or access to the Platform, the Services, or any other Content available via the Platform or the Services. All rights not expressly granted to You in these Terms and Conditions are reserved and retained by Company.  
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Submissions',
    chartObjects: [
      {
        id: '3.4',
        chartDescriptionArray: [
          {
            text: `You understand and agree that Company owns, or (where required, appropriate, or applicable) has been licensed by third parties to use, all right, title, and interest in and to the Platform and the Services, and all Content, as well as the design, structure, selection, coordination, expression, “look and feel,” and arrangement of such Content. The Platform and Services will remain the exclusive property of the Company. 
            `
          },
          {
            text: `You acknowledge that the Platform and the Services are proprietary to Company and are protected by applicable intellectual property laws and other proprietary rights, laws, and treaties of both the Country and other countries, and that You acquire no ownership interest in the Platform or the Services by accessing and using either.
            `
          },
          {
            text: `You agree that Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company. 
            `
          },
          {
            text: `Company grants You a personal, non-exclusive, non-transferable, revocable, limited license to use the Platform and the Services solely for Your personal or internal use and subject to the condition that You do not (and do not allow any third party to) copy, modify, create a derivative work from, reverse engineer, reverse assemble, or otherwise attempt to discover any source code, sell, assign, sublicense, grant a security interest in, or otherwise transfer any right in or access to the Platform, the Services, or any other Content available via the Platform or the Services. All rights not expressly granted to You in these Terms and Conditions are reserved and retained by Company.  
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Communications',
    chartObjects: [
      {
        id: '1',
        chartDescriptionArray: [
          {
            text: `You understand and agree that Company owns, or (where required, appropriate, or applicable) has been licensed by third parties to use, all right, title, and interest in and to the Platform and the Services, and all Content, as well as the design, structure, selection, coordination, expression, “look and feel,” and arrangement of such Content. The Platform and Services will remain the exclusive property of the Company. 
            `
          },
          {
            text: `You acknowledge that the Platform and the Services are proprietary to Company and are protected by applicable intellectual property laws and other proprietary rights, laws, and treaties of both the Country and other countries, and that You acquire no ownership interest in the Platform or the Services by accessing and using either.
            `
          },
          {
            text: `You agree that Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of the Company. 
            `
          },
          {
            text: `Company grants You a personal, non-exclusive, non-transferable, revocable, limited license to use the Platform and the Services solely for Your personal or internal use and subject to the condition that You do not (and do not allow any third party to) copy, modify, create a derivative work from, reverse engineer, reverse assemble, or otherwise attempt to discover any source code, sell, assign, sublicense, grant a security interest in, or otherwise transfer any right in or access to the Platform, the Services, or any other Content available via the Platform or the Services. All rights not expressly granted to You in these Terms and Conditions are reserved and retained by Company.  
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Disclaimers',
    chartObjects: [
      {
        id: '2',
        chartDescriptionArray: [
          {
            text: `The information and materials available through the Platform and the Services are for informational and educational purposes only. You acknowledge and agree that Your use of the Platform and Services is at your own risk.  
            `
          },
          {
            text: `THE INFORMATION, SOFTWARE, CONTENT, SERVICES, OR MATERIALS AVAILABLE FROM OR PROVIDED ON THE PLATFORM OR VIA THE SERVICES, ARE PROVIDED “AS IS” AND “AS AVAILABLE”, WITHOUT WARRANTIES OR GUARANTIES OF ANY KIND, EITHER EXPRESSED OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY, TITLE, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY WARRANTIES IMPLIED BY A COURSE OF PERFORMANCE, COURSE OF DEALING, OR USAGE OF TRADE. 
            `
          },
          {
            text: `COMPANY AND/OR OUR THIRD PARTY SUPPLIERS OR LICENSORS DO NOT REPRESENT, WARRANT OR COVENANT THAT THE PLATFORM AND THE SERVICES ARE OR WILL BE ACCURATE, CURRENT, COMPLETE, FREE OF TECHNICAL AND TYPOGRAPHICAL ERRORS, SECURE, RELIABLE, OR APPROPRIATE FOR ANY PARTICULAR USE TO WHICH YOU OR ANY THIRD PARTY MAY CHOOSE TO PUT THEM, THAT THEY ARE OR WILL BE AVAILABLE ON AN UNINTERRUPTED AND ERROR-FREE BASIS, THAT DEFECTS WILL BE CORRECTED, OR THAT THE SYSTEM AND SERVICES ARE FREE OF VIRUSES OR OTHER DISABLING DEVICES OR HARMFUL COMPONENTS. COMPANY PERIODICALLY AMENDS, CHANGES, ADDS, DELETES, UPDATES, OR ALTERS THE PLATFORM AND THE SERVICES WITHOUT NOTICE. FURTHER, COMPANY ASSUMES NO LIABILITY OR RESPONSIBILITY FOR ANY ERRORS OR OMISSIONS IN THE CONTENT OF THE PLATFORM OR THE SERVICES. COMPANY SPECIFICALLY DISCLAIMS ANY DUTY TO UPDATE THE CONTENT, OR ANY OTHER INFORMATION ON THE PLATFORM OR THE SERVICES.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Limitation of Liability',
    chartObjects: [
      {
        id: '3',
        chartDescriptionArray: [
          {
            text: `IN NO EVENT SHALL COMPANY, ITS CONTRACTORS, SUPPLIERS, CONTENT PROVIDERS, OR THE OFFICERS, DIRECTORS, EMPLOYEES, REPRESENTATIVES, AND AGENTS OF EACH OF THE FOREGOING, BE LIABLE TO YOU OR ANY OTHER THIRD PARTY FOR ANY LOSS, COST, DAMAGE, OR OTHER INJURY, WHETHER IN CONTRACT, TORT, NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE, ARISING OUT OF OR IN CONNECTION WITH: (A) THE USE OF, OR RELIANCE ON, THE PLATFORM AND/OR SERVICES; (B) THE SECURITY OF THE PLATFORM AND/OR THE SERVICES; (C) THE USE, COPYING, OR DISPLAY OF THE PLATFORM AND/OR THE SERVICES OR THE TRANSMISSION OF INFORMATION TO OR FROM THE PLATFORM OVER THE INTERNET; (D) COMPANY’S PERFORMANCE OF, OR FAILURE TO PERFORM, ITS OBLIGATIONS IN CONNECTION WITH THESE TERMS AND CONDITIONS AND THE SERVICES; (E) ANY DEFAMATORY, OFFENSIVE, OR ILLEGAL CONDUCT BY YOU, OTHER USERS OF THE SYSTEM OR THE SERVICES, OR OTHER THIRD PARTIES; OR (F) THE AVAILABILITY, RELIABILITY, ACCURACY, TIMELINESS, OR QUALITY OF THE SERVICES. UNDER NO CIRCUMSTANCES SHALL COMPANY, OR ITS CONTRACTORS, SUPPLIERS, CONTENT PROVIDERS, OR THE OFFICERS, DIRECTORS, EMPLOYEES AND AGENTS OF EACH OF THE FOREGOING, BE LIABLE TO YOU OR ANY OTHER THIRD PARTY FOR ANY INDIRECT, CONSEQUENTIAL, INCIDENTAL, PUNITIVE, SPECIAL, OR SIMILAR DAMAGES OR COSTS (INCLUDING, BUT NOT LIMITED TO, LOST PROFITS OR DATA, LOSS OF GOODWILL, LOSS OF OR DAMAGE TO PROPERTY, LOSS OF USE, BUSINESS INTERRUPTION, AND CLAIMS OF THIRD PARTIES) ARISING OUT OF OR IN CONNECTION WITH THESE TERMS AND CONDITIONS, THE PLATFORM, THE SERVICES, OR ANYTHING DESCRIBED IN THE FOREGOING CLAUSES (A) THROUGH (F) OF THIS PARAGRAPH, OR ANY OTHER CAUSE BEYOND THE CONTROL OF COMPANY, EVEN IF COMPANY WAS ADVISED, KNEW, OR SHOULD HAVE KNOWN OF THE POSSIBILITY OF SUCH DAMAGES OR COSTS. IN A JURISDICTION THAT DOES NOT ALLOW THE EXCLUSION OR LIMITATION OF LIABILITY FOR CERTAIN DAMAGES, THE AGGREGATE LIABILITY OF COMPANY AND ITS CONTRACTORS, SUPPLIERS, CONTENT PROVIDERS, AND THE OFFICERS, DIRECTORS, EMPLOYEES, REPRESENTATIVES, AND AGENTS OF EACH OF THE FOREGOING, SHALL BE LIMITED IN ACCORDANCE WITH THESE TERMS AND CONDITIONS TO THE FULLEST EXTENT PERMITTED BY LAW.`
          },
          {
            text: `WITHOUT LIMITING ANY OF THE FOREGOING, IF COMPANY, ITS CONTRACTORS, SUPPLIERS, CONTENT PROVIDERS, OR ANY OF THE OFFICERS, DIRECTORS, EMPLOYEES, REPRESENTATIVES, OR AGENTS OF ANY OF THE FOREGOING, IS FOUND LIABLE TO YOU OR TO ANY THIRD PARTY AS A RESULT OF ANY CLAIMS OR OTHER MATTERS ARISING UNDER OR IN CONNECTION WITH THESE TERMS AND CONDITIONS, THE PLATFORM, OR THE SERVICES, COMPANY AND SUCH PARTIES’ CUMULATIVE, AGGREGATE, AND MAXIMUM LIABILITY FOR ALL SUCH CLAIMS AND OTHER MATTERS IN ANY CALENDAR YEAR SHALL NOT EXCEED USD $1000.  
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Your Feedback to Us',
    chartObjects: [
      {
        id: '4',
        chartDescriptionArray: [
          {
            text: `You assign all rights, title and interest in any Feedback You provide the Company. If for any reason such assignment is ineffective, You agree to grant the Company a non-exclusive, perpetual, irrevocable, royalty free, worldwide right and license to use, reproduce, disclose, sub-license, distribute, modify and exploit such Feedback without restriction.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Links to Other Websites',
    chartObjects: [
      {
        id: '5',
        chartDescriptionArray: [
          {
            text: `For Your convenience, Our Platform may contain Linked Websites.`
          },
          {
            text: `The Company has no control over, and assumes no responsibility for, the availability, accuracy, content, privacy policies, or any other aspect or practices of any Linked Websites. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such Linked Websites. We make no representations or warranties with respect to any products or services made available, sold, or provided to You by any third party. Your use of Linked Websites and any purchases of products or services from such Linked Websites are subject to the terms and conditions of such other websites. You agree that You will bring no suit or claim against Company arising from or based upon any such use of any Linked Websites. Hyperlinks to such Linked Websites on the Platform and Services do not imply that:  (a) Company is affiliated or associated with any Linked Website; (b) Company is legally authorized to use any trademark, trade name, logo, or copyright symbol displayed in connection with or accessible through such links; or (c) any Linked Site is authorized to use any trademark, trade name, logo, or copyright symbol of Company.
            `
          },
          {
            text: `Company may use Your name, logo, trademarks or trade names in publicity releases, promotional material, customer lists, advertising, marketing or business-generating efforts, whether written or oral.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Disclaimer of Third Party Information',
    chartObjects: [
      {
        id: '6',
        chartDescriptionArray: [
          {
            text: `To the extent that any Third Party Materials are available on the Platform or are part of the Services, Company has no editorial control or responsibility over such Third Party Materials. Therefore, any opinions, statements, products, services or other Third Party Materials are those of the applicable third party. Company does not represent or endorse the accuracy or reliability of any opinion, statement or other information provided by any third party (including any Submissions), or represent or warrant that Your use of any Third Party Materials will not infringe rights of third parties not owned by or affiliated with Company.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Termination',
    chartObjects: [
      {
        id: '7',
        chartDescriptionArray: [
          {
            text: `We may terminate or suspend Your Organizational Account or Individual Account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.
            `
          },
          {
            text: `The administrator of Your Organizational Account that Your Individual Account may be associated with may terminate or suspend Your Individual Account without prior notice or liability for any reason whatsoever, including without limitation if You breach these Terms and Conditions.
            `
          },
          {
            text: (
              <>
                Upon termination, Your right to use the Platform will cease
                immediately. If You wish to terminate Your Account, You may
                simply discontinue using the Platform or You can contact us by
                email at{' '}
                {renderLink({
                  link: 'support@stellarsight.org',
                  isEmail: true
                })}
              </>
            )
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Governing Law/Jurisdiction/Claims Limitation',
    chartObjects: [
      {
        id: '8',
        chartDescriptionArray: [
          {
            text: `These Terms and Conditions shall be governed by and construed under the laws of the District of Columbia, without regard to conflicts of laws, principles or rules. Any legal action brought by You that arises out of or relates to these Terms and Conditions or Your access to and use of the Platform or the Services must be commenced within one year after the cause of action arises. You hereby expressly consent and irrevocably submit to the exclusive jurisdiction of the state and federal courts sitting in the District of Columbia for resolution of any matters related to interpretation, construction, or enforcement of these Terms and Conditions or otherwise in connection with these Terms and Conditions or otherwise related to or in connection with Your access to and use of the Platform or the Services. You further expressly waive any claim that venue is improper for any reason in these courts. 
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Dispute Resolution',
    chartObjects: [
      {
        id: '9',
        chartDescriptionArray: [
          {
            text: `If You have any concern or dispute about the Platform or Services, You agree to first try to resolve the dispute informally by contacting the Company using the contact information provided below.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Severability and Waiver',
    chartObjects: [
      {
        chartName: 'Severability',
        id: '3.5',
        chartDescriptionArray: [
          {
            text: `If any provision of these Terms and Conditions is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
            `
          }
        ]
      },
      {
        chartName: 'Waiver',
        id: '3.6',
        chartDescriptionArray: [
          {
            text: `Except as provided herein, the failure to exercise a right or to require performance of an obligation under this Terms and Conditions shall not effect a party's ability to exercise such right or require such performance at any time thereafter nor shall be the waiver of a breach constitute a waiver of any subsequent breach.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Translation Interpretation',
    chartObjects: [
      {
        id: '3.7',
        chartDescriptionArray: [
          {
            text: `These Terms and Conditions may have been translated if We have made them available to You on Our Platform.`
          },
          {
            text: `You agree that the original English text shall prevail in the case of a dispute.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Changes to These Terms and Conditions',
    chartObjects: [
      {
        id: '3.8',
        chartDescriptionArray: [
          {
            text: `We reserve the right, at Our sole discretion, to modify, update, or replace these Terms and Conditions at any time effective upon posting the revised Terms and Conditions, along with the date on which it was most recently updated, on the Website. You should view these Terms and Conditions often to stay informed of any changes that may affect You. Your continued use of the System and/or the Services thereafter signifies Your acceptance of such changes. By continuing to access or use Our Platform after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the Website and the Platform.
            `
          },
          {
            text: `The version of these Terms and Conditions posted on Our Website on each respective date You visit the Website will be the Terms and Conditions applicable to Your access and use of the Platform and Services on that date. Our electronically or otherwise properly stored copies of these Terms and Conditions shall be deemed to be the true, complete, valid, and authentic copies of the version of the Terms and Conditions that were in force on each respective date You visited the Website. We reserve the right to terminate these Terms and Conditions, or to refuse, restrict, or discontinue access to the Platform or use of the Services (or any portions, components, or features thereof) to You or any other person or entity, for any reason or for no reason whatsoever, at any time, without notice or liability.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'General',
    chartObjects: [
      {
        id: '3.9',
        chartDescriptionArray: [
          {
            text: `These Terms and Conditions and any applicable promotions terms (as each may be revised and amended from time to time according to their respective terms) collectively constitute the entire agreement with respect to Your access to and use of the Platform and the Services. You agree that You shall not contest the admissibility or enforceability of Company’s copy of these Terms and Conditions in connection with any action or proceeding arising out of or relating to these Terms and Conditions. Except as expressly provided for herein, these Terms and Conditions do not confer any rights, remedies, or benefits upon any person or entity other than You and Company. Company may assign its rights and duties under these Terms and Conditions at any time to any third party without notice. You may not assign these Terms and Conditions without the prior written consent of Company. These Terms and Conditions shall be binding on and inure to the benefit of the parties hereto and their respective successors and assigns. You are responsible for complying with any and all laws of the jurisdiction from which You are accessing the Platform and using the Services and any other jurisdiction whose laws apply to You or Your actions. Any rights not expressly granted herein are reserved.  Neither these Terms and Conditions, nor any of the Services, create any partnership, joint venture, employment, or other agency relationship between Company and You. You may not enter into any contract on Our behalf or bind Us in any way.`
          },
          {
            text: `You agree that any violation, or threatened violation, by You of these Terms and Conditions, constitutes an unlawful and unfair business practice that will cause Us irreparable and unquantifiable harm. You also agree that monetary damages would be inadequate for such harm and consent to Our obtaining any injunctive or equitable relief that We deem necessary or appropriate. These remedies are in addition to any other remedies We may have at law or in equity.
            `
          }
        ]
      }
    ]
  },
  {
    pageIntro: 'Contact Us',
    chartObjects: [
      {
        id: '3.10',
        chartDescriptionArray: [
          {
            text: (
              <>
                If You have any questions or comments about these Terms and
                Conditions, You can contact Us by email at{' '}
                {renderLink({
                  link: 'support@stellarsight.org',
                  isEmail: true
                })}
              </>
            )
          }
        ]
      }
    ]
  }
]

export { chartOverviewObjects, renderLink }
